// src/merch/MerchAdmin.jsx
//
// /admin — the review queue. Sign in by email code/link; access is gated
// by public.merch_admins (RLS + the merch-review function both check).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase, callFn } from "./supabase";
import { viewUrlFor } from "./designs";

const TABS = [
  { id: "review", label: "To review", statuses: ["awaiting_review"] },
  { id: "print", label: "To print / ship", statuses: ["approved", "printed"] },
  { id: "shipped", label: "Shipped", statuses: ["shipped"] },
  { id: "closed", label: "Rejected / expired", statuses: ["rejected", "expired"] },
];

const REJECT_REASONS = [
  "Your design includes a pro team or league logo/name, which we're not licensed to print.",
  "Your design includes a brand logo we're not allowed to reproduce.",
  "An uploaded image is too low-resolution to print clearly.",
  "Some of the text or imagery isn't appropriate for our products.",
];

const HOLD_MS = 7 * 24 * 3600 * 1000;
const LEVEL_ORDER = { high: 0, medium: 1, info: 2 };
const money = (c) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);

function holdLeft(o) {
  if (!o.authorized_at) return null;
  const ms = new Date(o.authorized_at).getTime() + HOLD_MS - Date.now();
  if (ms <= 0) return { text: "hold expired", urgent: true };
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000);
  return { text: `hold expires in ${d}d ${h}h`, urgent: ms < 2 * 86400000 };
}

function SignIn() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);
  async function send(e) {
    e.preventDefault();
    setErr(null);
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      // creating the account is harmless — access is granted separately
      // by adding the user to merch_admins
      options: { emailRedirectTo: window.location.href, shouldCreateUser: true },
    });
    if (error) setErr(error.message);
    else setSent(true);
  }
  return (
    <form className="ma-panel ma-admin-signin" onSubmit={send}>
      <h2>Reviewer sign-in</h2>
      {sent ? (
        <p>Check your email for a sign-in link.</p>
      ) : (
        <>
          <input type="email" required placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button className="ma-btn primary">Email me a sign-in link</button>
          {err && <div className="ma-note error">{err}</div>}
        </>
      )}
    </form>
  );
}

function OrderCard({ o, onChanged }) {
  const [urls, setUrls] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState(REJECT_REASONS[0]);
  const [tracking, setTracking] = useState("");
  const [notes, setNotes] = useState(o.admin_notes || "");

  useEffect(() => {
    const paths = Object.entries(o.files || {});
    if (!paths.length) return;
    supabase.storage.from("merch-orders")
      .createSignedUrls(paths.map(([, p]) => p), 3600)
      .then(({ data }) => {
        const m = {};
        (data || []).forEach((d, i) => { if (d.signedUrl) m[paths[i][0]] = d.signedUrl; });
        setUrls(m);
      });
  }, [o.id, o.files]);

  async function act(action, extra = {}) {
    setBusy(true);
    setErr(null);
    try {
      await callFn("merch-review", { orderId: o.id, action, ...extra });
      onChanged();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  const ref = o.id.slice(0, 8).toUpperCase();
  const hold = o.status === "awaiting_review" ? holdLeft(o) : null;
  const flags = [...(o.flags || [])].sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]);
  const addr = o.shipping_address;

  return (
    <article className="ma-order-card">
      <header>
        <b>#{ref}</b>
        <span className="ma-muted">{new Date(o.created_at).toLocaleString()}</span>
        {hold && <span className={`ma-chip ${hold.urgent ? "urgent" : ""}`}>{hold.text}</span>}
        <span className="ma-chip">{o.status.replace("_", " ")}</span>
      </header>

      <div className="ma-order-previews">
        {Object.keys(urls).filter((n) => n.endsWith(".png")).map((n) => (
          <figure key={n}>
            <img src={urls[n]} alt={n} />
            <figcaption>{n.replace(".png", "")}</figcaption>
          </figure>
        ))}
      </div>

      {flags.length > 0 && (
        <ul className="ma-flags">
          {flags.map((f, i) => <li key={i} className={f.level}>⚑ {f.detail}</li>)}
        </ul>
      )}

      <div className="ma-order-meta">
        <div><span>Store</span>{o.store || "merchmod"}</div>
        <div><span>Item</span>{o.quantity} × {o.product_id} · {o.size_id} · {o.colour_id} · {o.sides.join(" + ")}</div>
        {o.hand && <div><span>Shot</span><b>{o.hand === "left" ? "LEFT" : "RIGHT"}</b></div>}
        <div><span>Total</span>{money(o.total_cents)} <span className="ma-muted">(subtotal {money(o.subtotal_cents)})</span></div>
        <div><span>Customer</span>{o.customer_name || "—"} · {o.customer_email || "—"}</div>
        {addr && <div><span>Ship to</span>{[addr.line1, addr.line2, addr.city, addr.state, addr.postal_code, addr.country].filter(Boolean).join(", ")}</div>}
        {o.design_text?.length > 0 && <div><span>Text</span>{o.design_text.map((t) => `"${t}"`).join(" · ")}</div>}
        {o.reject_reason && <div><span>Rejected</span>{o.reject_reason}</div>}
        {o.tracking_number && <div><span>Tracking</span>{o.tracking_number}</div>}
      </div>

      <div className="ma-row">
        {urls["sheet.pdf"] && <a className="ma-btn" href={urls["sheet.pdf"]} target="_blank" rel="noreferrer">Print PDF ↓</a>}
        {o.design_id && <a className="ma-btn" href={viewUrlFor(o.design_id)} target="_blank" rel="noreferrer">3D view ↗</a>}
        {urls["design.json"] && <a className="ma-btn ghost" href={urls["design.json"]} target="_blank" rel="noreferrer">Design file</a>}
      </div>

      {o.status === "awaiting_review" && !rejecting && (
        <div className="ma-row">
          <button className="ma-btn primary" disabled={busy} onClick={() => window.confirm(`Approve and charge ${money(o.total_cents)}?`) && act("approve")}>
            ✓ Approve & charge
          </button>
          <button className="ma-btn ghost danger" disabled={busy} onClick={() => setRejecting(true)}>✕ Reject</button>
        </div>
      )}
      {rejecting && (
        <div className="ma-reject">
          {REJECT_REASONS.map((r) => (
            <label key={r}><input type="radio" checked={reason === r} onChange={() => setReason(r)} /> {r}</label>
          ))}
          <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="ma-row">
            <button className="ma-btn danger" disabled={busy || !reason.trim()} onClick={() => act("reject", { reason })}>Reject & release hold</button>
            <button className="ma-btn ghost" onClick={() => setRejecting(false)}>Cancel</button>
          </div>
        </div>
      )}
      {o.status === "approved" && (
        <div className="ma-row">
          <button className="ma-btn" disabled={busy} onClick={() => act("printed")}>Mark printed</button>
        </div>
      )}
      {(o.status === "approved" || o.status === "printed") && (
        <div className="ma-row">
          <input className="ma-input" placeholder="Tracking number (optional)" value={tracking} onChange={(e) => setTracking(e.target.value)} />
          <button className="ma-btn primary" disabled={busy} onClick={() => act("shipped", { tracking })}>Mark shipped</button>
        </div>
      )}

      <details>
        <summary className="ma-muted">Notes</summary>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        <button className="ma-btn ghost" disabled={busy} onClick={() => act("note", { notes })}>Save note</button>
      </details>

      {err && <div className="ma-note error">{err}</div>}
    </article>
  );
}

// Opted-in marketing contacts → CSV (email, when + where they opted in).
// Only people who ticked the opt-in box; unsubscribes are excluded.
async function exportContacts() {
  const { data, error } = await supabase
    .from("merch_contacts")
    .select("email, opt_in_at, opt_in_source, created_at")
    .eq("marketing_opt_in", true)
    .order("opt_in_at", { ascending: true });
  if (error) return window.alert(error.message);
  const rows = [["email", "opted_in_at", "source", "first_seen"], ...(data || []).map((c) => [c.email, c.opt_in_at, c.opt_in_source, c.created_at])];
  const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `merchmod-optins-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

export default function MerchAdmin({ homeHref }) {
  const [session, setSession] = useState(undefined);
  const [isAdmin, setIsAdmin] = useState(null);
  const [tab, setTab] = useState("review");
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) return;
    supabase.from("merch_admins").select("user_id").eq("user_id", session.user.id).maybeSingle()
      .then(({ data }) => setIsAdmin(!!data));
  }, [session]);

  const load = useCallback(async () => {
    setLoading(true);
    const statuses = TABS.find((t) => t.id === tab).statuses;
    const { data } = await supabase.from("merch_orders").select("*").in("status", statuses)
      .order("created_at", { ascending: tab === "review" }).limit(200);
    let rows = data || [];
    if (tab === "review") {
      // most serious flag first, then oldest (closest to hold expiry)
      const worst = (o) => Math.min(3, ...(o.flags || []).map((f) => LEVEL_ORDER[f.level] ?? 3));
      rows = rows.sort((a, b) => worst(a) - worst(b) || new Date(a.created_at) - new Date(b.created_at));
    }
    setOrders(rows);
    const c = {};
    await Promise.all(TABS.map(async (t) => {
      const { count } = await supabase.from("merch_orders").select("id", { count: "exact", head: true }).in("status", t.statuses);
      c[t.id] = count || 0;
    }));
    setCounts(c);
    setLoading(false);
  }, [tab]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const body = useMemo(() => {
    if (session === undefined) return <p className="ma-muted">Loading…</p>;
    if (!session) return <SignIn />;
    if (isAdmin === null) return <p className="ma-muted">Checking access…</p>;
    if (!isAdmin) return (
      <div className="ma-panel">
        <p>{session.user.email} isn't a reviewer.</p>
        <button className="ma-btn ghost" onClick={() => supabase.auth.signOut()}>Sign out</button>
      </div>
    );
    return (
      <>
        <div className="ma-chips ma-admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} aria-pressed={t.id === tab} onClick={() => setTab(t.id)}>
              {t.label}{counts[t.id] ? ` (${counts[t.id]})` : ""}
            </button>
          ))}
          <button onClick={load} disabled={loading}>{loading ? "…" : "↻"}</button>
          <button onClick={exportContacts} title="Download everyone who opted in to news & offers">Mailing list ↓</button>
        </div>
        {!loading && orders.length === 0 && <p className="ma-muted">Nothing here. 🎉</p>}
        <div className="ma-order-list">
          {orders.map((o) => <OrderCard key={o.id} o={o} onChanged={load} />)}
        </div>
      </>
    );
  }, [session, isAdmin, tab, orders, counts, loading, load]);

  return (
    <div className="ma-app">
      <header className="ma-header">
        <a href={homeHref}><img className="ma-logo" src="/merch/MerchMod_Logo.svg" alt="MerchMod" /></a>
        <div className="ma-sub">Review queue</div>
        {session && <button className="ma-btn ghost ma-admin-out" onClick={() => supabase.auth.signOut()}>Sign out</button>}
      </header>
      <main className="ma-admin">{body}</main>
    </div>
  );
}
